import { apiGet, apiPost } from '../../../adapters/api-client/api';

/**
 * MC-04 — the optional follow-up after a learner missed a delivered class.
 *
 * The teacher picks an approved message by id; the server owns the text,
 * sends it to the guardian and refuses anything else in the request. There is
 * deliberately no way to send a body from here.
 */

export type FollowUpBlock =
  | 'NOT_A_SESSION'
  | 'SESSION_NOT_DELIVERED'
  | 'SESSION_NOT_ENDED'
  | 'ATTENDANCE_PENDING'
  | 'LEARNER_ATTENDED';

export interface FollowUpOption {
  messageId: string;
  version: number;
  label: string;
  preview: string;
  approval: 'PROPOSED_PENDING_PRODUCT_APPROVAL';
}

export interface SentFollowUp {
  sequence: number;
  messageId: string;
  messageVersion: number;
  body: string;
  sentAt: string;
  emailStatus: 'PENDING' | 'SENT' | 'FAILED';
}

export interface TeacherFollowUpView {
  enrollmentId: string;
  sequence: number;
  catalogueVersion: string;
  recipient: string;
  block: FollowUpBlock | null;
  options: FollowUpOption[];
  sent: SentFollowUp | null;
}

export interface GuardianFollowUp {
  sequence: number;
  body: string;
  sentAt: string;
}

const base = (enrollmentId: string, sequence: number) =>
  `/api/v1/enrollments/${enrollmentId}/sessions/${sequence}/absence-follow-up`;

export function getTeacherFollowUp(
  enrollmentId: string,
  sequence: number,
): Promise<TeacherFollowUpView> {
  return apiGet(base(enrollmentId, sequence));
}

/** Only the message id travels. Idempotent per session: a retry returns the first send. */
export function sendFollowUp(
  enrollmentId: string,
  sequence: number,
  messageId: string,
): Promise<SentFollowUp & { replay: boolean }> {
  return apiPost(base(enrollmentId, sequence), { messageId });
}

/** The guardian's copy of the teacher's follow-ups on one enrollment. */
export function getGuardianFollowUps(
  enrollmentId: string,
): Promise<{ followUps: GuardianFollowUp[] }> {
  return apiGet(`/api/v1/enrollments/${enrollmentId}/absence-follow-ups`);
}
